(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = factory;
    return;
  }

  if (root.CentralSportExpressDeliveryPopup) return;
  root.CentralSportExpressDeliveryPopup = factory(root);
})(typeof window !== 'undefined' ? window : globalThis, function createExpressDeliveryPopup(runtime) {
  'use strict';

  var ELEMENT_NAME = 'cs-express-delivery-popup';
  var DELIVERY_EVENT = 'cs:express-delivery:updated';
  var STORAGE_PREFIX = 'cs:express-popup:';
  var STORAGE_VERSION = 'express-v1';
  var DEFAULT_FREQUENCY = 'once_per_session';
  var DEFAULT_COOLDOWN_DAYS = 7;
  var memorySessionSeen = Object.create(null);
  var memoryLastShown = Object.create(null);
  var primaryInstance = null;
  var instances = [];

  function presentation(engine, data) {
    data = data || {};
    function rich(status, hasContent) {
      return { ready: true, visible: hasContent === true || hasContent === 'true', status: status, template: status };
    }
    if (!engine || typeof engine.resolveDelivery !== 'function') {
      return { ready: false, visible: false, status: 'pending', title: '', description: '' };
    }

    try {
      var result = engine.resolveDelivery({ product: { eligible: true, available: true } });
      var status = result && (result.state || result.status);
      if (!result) return { ready: false, visible: false, status: 'pending', title: '', description: '' };
      if (result.visible === false) {
        return { ready: true, visible: false, status: status || 'unavailable', template: null };
      }
      if (status === 'today' || status === 'same_day') {
        return rich('same_day', data.todayHasContent);
      }
      if (status === 'next_business_day') {
        return rich('next_business_day', data.nextHasContent);
      }
      if (status === 'weekend') return rich('weekend', data.weekendHasContent);
      if (status === 'holiday' || status === 'blackout') return rich(status, data.nonOperationalHasContent);
      return rich('fallback', data.fallbackHasContent);
    } catch (error) {
      return rich('fallback', data.fallbackHasContent);
    }
  }

  function normalizeFrequency(value) {
    return value === 'every_page' || value === 'once_per_session' || value === 'cooldown_days'
      ? value
      : DEFAULT_FREQUENCY;
  }

  function normalizeCooldownDays(value) {
    var days = Number(value);
    if (!Number.isFinite(days) || days < 1 || days > 30) return DEFAULT_COOLDOWN_DAYS;
    return Math.floor(days);
  }

  function frequencyConfig(element) {
    var dataset = element && element.dataset ? element.dataset : {};
    return {
      frequency: normalizeFrequency(dataset.frequency),
      cooldownDays: normalizeCooldownDays(dataset.cooldownDays),
      sessionKey: STORAGE_PREFIX + 'session:' + STORAGE_VERSION,
      cooldownKey: STORAGE_PREFIX + 'last-shown:' + STORAGE_VERSION
    };
  }

  function getStorage(type) {
    if (!runtime) return null;
    try {
      var storage = runtime[type];
      if (!storage) return null;
      storage.getItem(STORAGE_PREFIX + 'availability');
      return storage;
    } catch (error) {
      return null;
    }
  }

  function isFrequencyEligible(element, now) {
    var config = frequencyConfig(element);
    if (config.frequency === 'every_page') return !element.csShownOnPage;
    if (config.frequency === 'once_per_session') {
      var sessionStorage = getStorage('sessionStorage');
      if (!sessionStorage) return !memorySessionSeen[config.sessionKey];
      try { return sessionStorage.getItem(config.sessionKey) !== 'shown'; }
      catch (error) { return !memorySessionSeen[config.sessionKey]; }
    }

    var localStorage = getStorage('localStorage');
    var rawTimestamp = null;
    if (localStorage) {
      try { rawTimestamp = localStorage.getItem(config.cooldownKey); }
      catch (error) { rawTimestamp = memoryLastShown[config.cooldownKey]; }
    } else {
      rawTimestamp = memoryLastShown[config.cooldownKey];
    }
    if (rawTimestamp == null || rawTimestamp === '') return true;
    var timestamp = Number(rawTimestamp);
    if (!Number.isFinite(timestamp) || timestamp < 0) return true;
    return (Number(now) || Date.now()) - timestamp >= config.cooldownDays * 24 * 60 * 60 * 1000;
  }

  function markFrequencyShown(element, now) {
    var config = frequencyConfig(element);
    element.csShownOnPage = true;
    if (config.frequency === 'every_page') return;
    if (config.frequency === 'once_per_session') {
      memorySessionSeen[config.sessionKey] = true;
      var sessionStorage = getStorage('sessionStorage');
      if (!sessionStorage) return;
      try { sessionStorage.setItem(config.sessionKey, 'shown'); } catch (error) { /* Memory fallback remains active. */ }
      return;
    }

    var timestamp = String(Number(now) || Date.now());
    memoryLastShown[config.cooldownKey] = timestamp;
    var localStorage = getStorage('localStorage');
    if (!localStorage) return;
    try { localStorage.setItem(config.cooldownKey, timestamp); } catch (error) { /* Memory fallback remains active. */ }
  }

  function isFocusable(element) {
    if (!element || element.disabled || element.hidden) return false;
    return !element.getAttribute || element.getAttribute('tabindex') !== '-1';
  }

  function focusableElements(dialog) {
    if (!dialog || typeof dialog.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
    )).filter(isFocusable);
  }

  function sectionEventMatches(event, sectionId) {
    if (!event || !sectionId) return false;
    var detailId = event.detail && event.detail.sectionId
      ? String(event.detail.sectionId)
      : '';
    var targetId = event.target && event.target.id
      ? String(event.target.id)
      : '';
    return detailId === sectionId
      || detailId.indexOf(sectionId) !== -1
      || targetId === 'shopify-section-' + sectionId
      || targetId.indexOf(sectionId) !== -1;
  }

  function isModalActive(candidate) {
    if (!candidate || candidate.hidden) return false;
    if (candidate.getAttribute) {
      if (candidate.getAttribute('aria-hidden') === 'true' || candidate.getAttribute('inert') != null) return false;
    }

    if (typeof candidate.closest === 'function') {
      if (candidate.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
      var cartDrawer = candidate.closest('cart-drawer');
      if (cartDrawer) return Boolean(cartDrawer.hasAttribute && cartDrawer.hasAttribute('open'));
      var drawer = candidate.closest('.drawer__container, .drawer__wrapper');
      if (drawer && drawer.classList
        && !drawer.classList.contains('is-open')
        && !drawer.classList.contains('is-visible')) return false;
    }

    if (runtime && typeof runtime.getComputedStyle === 'function') {
      var style = runtime.getComputedStyle(candidate);
      if (style && (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0)) return false;
    }
    if (typeof candidate.getClientRects === 'function' && candidate.getClientRects().length === 0) return false;
    return true;
  }

  function hasConflictingModal(element) {
    if (!runtime || !runtime.document) return false;
    var documentRef = runtime.document;
    var dialog = element && element.querySelector('[data-cs-express-delivery-popup-dialog]');
    var candidates = typeof documentRef.querySelectorAll === 'function'
      ? documentRef.querySelectorAll('[role="dialog"][aria-modal="true"]')
      : [];

    for (var index = 0; index < candidates.length; index += 1) {
      var candidate = candidates[index];
      if (candidate === dialog || element && typeof element.contains === 'function' && element.contains(candidate)) continue;
      if (isModalActive(candidate)) return true;
    }

    var body = documentRef.body;
    if (!body || !body.classList) return false;
    if (element && element.csOpen) return false;
    return body.classList.contains('overflow-hidden')
      || body.classList.contains('overflow-y-hidden')
      || Boolean(body.style && body.style.overflow === 'hidden');
  }

  function register() {
    if (!runtime || !runtime.customElements || !runtime.HTMLElement || runtime.customElements.get(ELEMENT_NAME)) return;

    class ExpressDeliveryPopup extends runtime.HTMLElement {
      constructor() {
        super();
        this.csConnected = false;
        this.csOpen = false;
        this.csTimer = null;
        this.csPreviousFocus = null;
        this.csScrollState = null;
        this.csEditorSelected = false;
        this.csShownOnPage = false;
        this.csPresentation = null;
        this.csOnDelivery = () => this.csEvaluate(false);
        this.csOnKeydown = (event) => this.csHandleKeydown(event);
        this.csOnClose = () => this.csClose({ restoreFocus: true });
        this.csOnBackdrop = (event) => {
          if (event.target === this.csBackdrop && this.dataset.closeOutside === 'true') {
            this.csClose({ restoreFocus: true });
          }
        };
        this.csOnSectionSelect = (event) => this.csHandleSectionSelect(event, true);
        this.csOnSectionDeselect = (event) => this.csHandleSectionSelect(event, false);
        this.csOnSectionLoad = (event) => this.csHandleSectionLoad(event);
        this.csOnSectionUnload = (event) => this.csHandleSectionUnload(event);
      }

      connectedCallback() {
        if (this.csConnected) return;
        this.csConnected = true;
        instances.push(this);
        if (primaryInstance && primaryInstance !== this) {
          this.csDuplicate = true;
          this.dataset.csDuplicate = 'true';
          this.hidden = true;
          this.setAttribute('aria-hidden', 'true');
          this.csUpdateDuplicateNote(true);
          return;
        }
        this.csActivate();
      }

      csActivate() {
        primaryInstance = this;
        this.csDuplicate = false;
        delete this.dataset.csDuplicate;
        this.csUpdateDuplicateNote(false);
        this.csDialog = this.querySelector('[data-cs-express-delivery-popup-dialog]');
        this.csBackdrop = this.querySelector('[data-cs-express-delivery-popup-backdrop]');
        this.csCloseButton = this.querySelector('[data-cs-express-delivery-popup-close]');

        runtime.document.addEventListener(DELIVERY_EVENT, this.csOnDelivery);
        runtime.document.addEventListener('shopify:section:select', this.csOnSectionSelect);
        runtime.document.addEventListener('shopify:section:deselect', this.csOnSectionDeselect);
        runtime.document.addEventListener('shopify:section:load', this.csOnSectionLoad);
        runtime.document.addEventListener('shopify:section:unload', this.csOnSectionUnload);
        if (this.csCloseButton) this.csCloseButton.addEventListener('click', this.csOnClose);
        if (this.csBackdrop) this.csBackdrop.addEventListener('click', this.csOnBackdrop);
        this.csEvaluate(false);
      }

      disconnectedCallback() {
        this.csRelease();
      }

      csUpdateDuplicateNote(show) {
        var note = this.nextElementSibling;
        if (!note || !note.hasAttribute
          || !note.hasAttribute('data-cs-express-delivery-popup-duplicate-note')) return;
        note.hidden = !(show && this.csIsDesignMode());
      }

      csRelease() {
        var wasPrimary = primaryInstance === this;
        this.csTeardown();
        instances = instances.filter((instance) => instance !== this);
        if (!wasPrimary) return;
        primaryInstance = null;
        var replacement = instances.find((instance) => instance.csConnected && instance.csDuplicate);
        if (replacement) replacement.csActivate();
      }

      csIsDesignMode() {
        return this.dataset.designMode === 'true'
          || Boolean(runtime.Shopify && runtime.Shopify.designMode);
      }

      csMatchesSection(event) {
        return sectionEventMatches(event, this.dataset.sectionId);
      }

      csHandleSectionSelect(event, selected) {
        if (!this.csMatchesSection(event)) return;
        this.csEditorSelected = selected;
        if (selected) {
          this.csEvaluate(true, { forceEditorPreview: true, ignoreConflicts: true });
        } else {
          this.csClose({ restoreFocus: false });
        }
      }

      csHandleSectionLoad(event) {
        if (!this.csMatchesSection(event)) return;
        this.csEvaluate(this.csIsDesignMode());
      }

      csHandleSectionUnload(event) {
        if (!this.csMatchesSection(event)) return;
        this.csRelease();
      }

      csEvaluate(immediate, options) {
        if (!this.csConnected) return;
        options = options || {};
        var result = presentation(runtime.CentralSportExpressDelivery, this.dataset || {});
        var editorPreview = this.csIsDesignMode()
          && (options.forceEditorPreview || this.dataset.showInEditor === 'true' || this.csEditorSelected);
        if (editorPreview && (!result.ready || !result.visible)) {
          result = {
            ready: true,
            visible: true,
            status: 'editor_preview',
            template: 'fallback'
          };
        }
        this.csPresentation = result;
        this.dataset.csStatus = result.status;
        this.csUpdateContent(result);

        if (!result.ready || !result.visible) {
          this.csCancelTimer();
          this.csClose({ restoreFocus: this.csOpen });
          return;
        }
        if (this.csOpen) return;

        var editorVisible = editorPreview;
        if (!editorVisible && !isFrequencyEligible(this, Date.now())) return;
        if (immediate || editorVisible) {
          this.csOpenPopup({ ignoreConflicts: options.ignoreConflicts === true });
          return;
        }
        this.csScheduleOpen();
      }

      csUpdateContent(result) {
        var title = this.querySelector('[data-cs-express-delivery-popup-title]');
        var description = this.querySelector('[data-cs-express-delivery-popup-description]');
        var fields = [
          { element: title, suffix: 'title' },
          { element: description, suffix: 'description' }
        ];
        for (var index = 0; index < fields.length; index += 1) {
          var field = fields[index];
          if (!field.element) continue;
          var template = result.template
            ? this.querySelector('[data-cs-express-delivery-popup-template="' + result.template + '-' + field.suffix + '"]')
            : null;
          if (template && template.content) field.element.replaceChildren(template.content.cloneNode(true));
          else field.element.replaceChildren();
          field.element.hidden = !field.element.textContent.replace(/\u00a0/g, ' ').trim();
        }
      }

      csScheduleOpen() {
        if (this.csTimer != null) return;
        var delaySeconds = Math.max(0, Math.min(Number(this.dataset.delay) || 0, 60));
        var setTimer = runtime.setTimeout || setTimeout;
        this.csTimer = setTimer(() => {
          this.csTimer = null;
          this.csOpenPopup();
        }, delaySeconds * 1000);
      }

      csCancelTimer() {
        if (this.csTimer == null) return;
        var clearTimer = runtime.clearTimeout || clearTimeout;
        clearTimer(this.csTimer);
        this.csTimer = null;
      }

      csOpenPopup(options) {
        options = options || {};
        if (!this.csConnected || this.csOpen || !this.csPresentation || !this.csPresentation.visible) return false;
        var bypassFrequency = this.csIsDesignMode();
        if (!bypassFrequency && !isFrequencyEligible(this, Date.now())) return false;
        if (!options.ignoreConflicts && hasConflictingModal(this)) {
          this.dataset.csBlockedByModal = 'true';
          return false;
        }

        delete this.dataset.csBlockedByModal;
        this.csCancelTimer();
        this.csPreviousFocus = runtime.document.activeElement;
        this.hidden = false;
        this.removeAttribute('aria-hidden');
        this.csOpen = true;
        this.dataset.csOpen = 'true';
        this.csLockScroll();
        runtime.document.addEventListener('keydown', this.csOnKeydown);
        if (!bypassFrequency) markFrequencyShown(this, Date.now());

        var focusTarget = this.csCloseButton || this.csDialog;
        var focus = function () { if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus(); };
        if (typeof runtime.requestAnimationFrame === 'function') runtime.requestAnimationFrame(focus);
        else focus();
        return true;
      }

      csClose(options) {
        options = options || {};
        this.csCancelTimer();
        if (!this.csOpen) {
          this.hidden = true;
          this.setAttribute('aria-hidden', 'true');
          return;
        }

        this.csOpen = false;
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        delete this.dataset.csOpen;
        runtime.document.removeEventListener('keydown', this.csOnKeydown);
        this.csUnlockScroll();

        var previousFocus = this.csPreviousFocus;
        this.csPreviousFocus = null;
        if (options.restoreFocus !== false && previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
      }

      csHandleKeydown(event) {
        if (!this.csOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.csClose({ restoreFocus: true });
          return;
        }
        if (event.key !== 'Tab') return;

        var focusables = focusableElements(this.csDialog);
        if (!focusables.length) {
          event.preventDefault();
          if (this.csDialog && typeof this.csDialog.focus === 'function') this.csDialog.focus();
          return;
        }
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && runtime.document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && runtime.document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      csLockScroll() {
        var body = runtime.document.body;
        if (!body || !body.style || this.csScrollState) return;
        var viewportWidth = Number(runtime.innerWidth) || 0;
        var documentWidth = runtime.document.documentElement
          ? Number(runtime.document.documentElement.clientWidth) || viewportWidth
          : viewportWidth;
        var scrollbarWidth = Math.max(0, viewportWidth - documentWidth);
        var computedPadding = 0;
        if (typeof runtime.getComputedStyle === 'function') {
          computedPadding = parseFloat(runtime.getComputedStyle(body).paddingRight) || 0;
        }
        this.csScrollState = {
          overflow: body.style.overflow || '',
          paddingRight: body.style.paddingRight || ''
        };
        body.style.overflow = 'hidden';
        if (scrollbarWidth) body.style.paddingRight = (computedPadding + scrollbarWidth) + 'px';
      }

      csUnlockScroll() {
        var body = runtime.document.body;
        if (!body || !body.style || !this.csScrollState) return;
        body.style.overflow = this.csScrollState.overflow;
        body.style.paddingRight = this.csScrollState.paddingRight;
        this.csScrollState = null;
      }

      csTeardown() {
        if (!this.csConnected) return;
        this.csUpdateDuplicateNote(false);
        if (this.csDuplicate) {
          this.csConnected = false;
          return;
        }
        this.csCancelTimer();
        this.csClose({ restoreFocus: false });
        runtime.document.removeEventListener(DELIVERY_EVENT, this.csOnDelivery);
        runtime.document.removeEventListener('shopify:section:select', this.csOnSectionSelect);
        runtime.document.removeEventListener('shopify:section:deselect', this.csOnSectionDeselect);
        runtime.document.removeEventListener('shopify:section:load', this.csOnSectionLoad);
        runtime.document.removeEventListener('shopify:section:unload', this.csOnSectionUnload);
        runtime.document.removeEventListener('keydown', this.csOnKeydown);
        if (this.csCloseButton) this.csCloseButton.removeEventListener('click', this.csOnClose);
        if (this.csBackdrop) this.csBackdrop.removeEventListener('click', this.csOnBackdrop);
        this.csConnected = false;
      }
    }

    runtime.customElements.define(ELEMENT_NAME, ExpressDeliveryPopup);
  }

  register();

  return Object.freeze({
    namespace: 'cs-express-delivery-popup',
    eventName: DELIVERY_EVENT,
    storagePrefix: STORAGE_PREFIX,
    storageVersion: STORAGE_VERSION,
    defaultFrequency: DEFAULT_FREQUENCY,
    defaultCooldownDays: DEFAULT_COOLDOWN_DAYS,
    presentation: presentation,
    normalizeFrequency: normalizeFrequency,
    normalizeCooldownDays: normalizeCooldownDays,
    frequencyConfig: frequencyConfig,
    isFrequencyEligible: isFrequencyEligible,
    markFrequencyShown: markFrequencyShown,
    focusableElements: focusableElements,
    sectionEventMatches: sectionEventMatches,
    isModalActive: isModalActive,
    hasConflictingModal: hasConflictingModal
  });
});
